import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, torus, textSprite } from '../core/meshkit.js';
import { CARS } from '../entities/cars.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, checkNpcClick } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';
import { getCrumbs, addCrumbs, buyCar, equipCar, ownsCar, getEquippedCar, getGarage } from '../core/save.js';
import { QUESTS, questsForGiver, acceptQuest, turnIn, objectiveProgress, flagObjective } from '../core/quests.js';

// Pretzel Pit Stop — buy and equip cars that boost walk speed.

export function createGarageScene(){
  return () => new GarageScene();
}

class GarageScene {
  constructor(){
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xE6E1D8);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 14;
    this.camTarget = new THREE.Vector3(0, 1.5, 0);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildRoom();

    this.pretzel = this._makePretzel();
    this.scene.add(this.pretzel.root);

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 4);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.carMeshes = [];
    CARS.forEach((c, i) => {
      const g = this._makeCarMesh(c.color);
      g.position.set((i - 1) * 3.2, 0, -1.5);
      g.rotation.y = -Math.PI / 6;
      this.scene.add(g);
      this.carMeshes.push(g);
      const label = textSprite(c.name, 28, '#3a2e2a', 2.6, 0.7);
      label.position.set(0, 1.4, 1.4); g.add(label);
    });

    this.hotspots = [
      { pos: this.pretzel.root.position.clone(), r: 2.4, label: '🗣 talk to Pit Crew Pretzel', action: () => this._talkToPretzel() },
      { pos: new THREE.Vector3(0, 0, 5.6), r: 2.2, label: '← back to Uptown', action: () => this.manager.goTo('uptown', { from: 'garage' }) },
      { pos: new THREE.Vector3(6.8, 0, -1.5), r: 2.4, label: '🏁 back door → Speedway', action: () => this.manager.goTo('speedway', { from: 'garage' }) }
    ];
    this.nearSpot = null;
    this.panels = [];
    this.state = 'explore';
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFF6E8, 0.72));
    const sun = new THREE.DirectionalLight(0xFFF2D8, 0.85);
    sun.position.set(5, 12, 7); sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const s = 16;
    sun.shadow.camera.left = -s; sun.shadow.camera.right = s;
    sun.shadow.camera.top = s; sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
  }

  _buildRoom(){
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(16, 14), new THREE.MeshLambertMaterial({ color: 0xC9C5BC }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);
    // service bay stripes
    for (let x = -6; x <= 6; x += 1.2) box(0.5, 0.02, 10, 0xA0A0A0, x, 0.02, -1, this.scene);
    // counter
    box(4, 1.0, 0.8, 0x6E4526, 0, 0.5, -5.5, this.scene);
    const sign = textSprite('Pretzel Pit Stop', 40, '#6E4526', 5, 1.2);
    sign.position.set(0, 2.8, -5.3); this.scene.add(sign);
  }

  _makeCarMesh(color){
    const g = new THREE.Group();
    // body
    box(1.6, 0.5, 2.6, color, 0, 0.55, 0, g);
    // cabin
    box(1.2, 0.5, 1.4, 0xDDF3FF, 0, 1.0, -0.2, g);
    // wheels
    let w = cyl(0.28, 0.28, 0.2, 0x2A2A2A, -0.85, 0.28,  0.9, g, 16); w.rotation.z = Math.PI/2;
    w = cyl(0.28, 0.28, 0.2, 0x2A2A2A,  0.85, 0.28,  0.9, g, 16); w.rotation.z = Math.PI/2;
    w = cyl(0.28, 0.28, 0.2, 0x2A2A2A, -0.85, 0.28, -0.9, g, 16); w.rotation.z = Math.PI/2;
    w = cyl(0.28, 0.28, 0.2, 0x2A2A2A,  0.85, 0.28, -0.9, g, 16); w.rotation.z = Math.PI/2;
    return g;
  }

  _makePretzel(){
    const build = (g) => {
      // pretzel twist
      torus(.32, .10, 0xA67C52, 0, .62, 0, g);
      sphere(.24, 0xD9A441, 0, 1.16, 0, g, 16);
      box(.16, .05, .05, 0x2E2018, 0, 1.06, .22, g);
      box(.04, .04, .04, 0x2E2018, -.07, 1.18, .18, g);
      box(.04, .04, .04, 0x2E2018,  .07, 1.18, .18, g);
      cyl(.03, .03, .34, C.cream, -.42, .72, .1, g, 6);
      cyl(.03, .03, .34, C.cream,  .42, .72, .1, g, 6);
      cyl(.06, .06, .38, 0x3D5FC0, -.12, .19, 0, g, 6);
      cyl(.06, .06, .38, 0x3D5FC0,  .12, .19, 0, g, 6);
      const cap = cyl(.3, .3, .1, 0xC9302C, 0, 1.48, 0, g, 16);
    };
    return new Townsperson({
      name: 'Pit Crew Pretzel', line: 'need a tune-up?',
      pos: new THREE.Vector3(0, 0, -4), facing: 0, wander: 0, speed: 0, bubbleY: 2.6, build
    });
  }

  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  _talkToPretzel(){
    this.bread.stop();
    this.bread.drawFace('happy');
    this.pretzelTab = 'quests';
    this._renderPretzelDialog();
  }

  _renderPretzelDialog(){
    this._clearPanels();
    const q = questsForGiver('pretzel');
    const activeRows = q.active.map(id => this._questRow(id)).join('');
    const availRows = q.available.map(id => this._questRow(id, true)).join('');
    const questBody = activeRows || availRows || '<p class="cafe-sub">All tuned up. Want a new ride?</p>';
    const carBody = CARS.map(c => {
      const owned = ownsCar(c.id);
      const equipped = getEquippedCar() === c.id;
      const afford = getCrumbs() >= c.price;
      let btn = '';
      if (equipped) btn = '<span style="font-weight:700;color:var(--sprout)">equipped</span>';
      else if (owned) btn = `<button class="cafe-btn equip-car" data-id="${c.id}">equip</button>`;
      else btn = `<button class="cafe-btn buy-car ${afford ? '' : 'disabled'}" data-id="${c.id}">🍞 ${c.price}</button>`;
      return `<div class="car-card ${owned ? 'owned' : ''} ${equipped ? 'equipped' : ''}"><div class="car-preview" style="background:${'#'+c.color.toString(16).padStart(6,'0')}">${c.icon}</div><div class="car-info"><b>${c.name}</b><span>${c.desc}</span></div><div>${btn}</div></div>`;
    }).join('');

    const p = this._panel(`
      <div class="cafe-card q-dialog" style="width:min(92vw,460px)">
        <h2>Pit Crew Pretzel</h2>
        <div class="cafe-sub">Pretzel Pit Stop</div>
        <div class="q-tabs">
          <button data-tab="quests" class="cafe-btn ghost ${this.pretzelTab === 'quests' ? 'active' : ''}">quests</button>
          <button data-tab="cars" class="cafe-btn ghost ${this.pretzelTab === 'cars' ? 'active' : ''}">cars</button>
        </div>
        <div class="q-tab-body">${this.pretzelTab === 'quests' ? questBody : `<div class="garage-list">${carBody}</div>`}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>
    `);
    p.querySelectorAll('[data-tab]').forEach(b => b.onclick = () => { this.pretzelTab = b.dataset.tab; this._renderPretzelDialog(); });
    p.querySelector('.close').onclick = () => this._clearPanels();
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this._renderPretzelDialog(); });
    p.querySelectorAll('.turnin').forEach(b => {
      b.onclick = () => { turnIn(b.dataset.id); HUD.refreshCrumbs(); HUD.refreshQuests(); this._renderPretzelDialog(); };
    });
    p.querySelectorAll('.buy-car').forEach(b => b.onclick = () => this._buyCar(b.dataset.id));
    p.querySelectorAll('.equip-car').forEach(b => b.onclick = () => this._equipCar(b.dataset.id));
  }

  _buyCar(id){
    const c = CARS.find(x => x.id === id);
    if (!c) return;
    if (buyCar(id, c.price)){
      flagObjective('first-car', 'boughtCar');
      HUD.refreshCrumbs();
      HUD.say(`You bought the ${c.name}!`);
    } else {
      HUD.say('Not enough crumbs for that ride.');
    }
    this._renderPretzelDialog();
  }

  _equipCar(id){
    if (equipCar(id)){
      HUD.say(`Equipped the ${CARS.find(c => c.id === id)?.name || 'car'}.`);
    }
    this._renderPretzelDialog();
  }

  _questRow(id, accept = false){
    const q = QUESTS[id];
    if (accept) return `<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}</span></div><button class="cafe-btn accept" data-id="${id}">accept</button></div>`;
    const { have, goal, ready } = objectiveProgress(id);
    return `<div class="q-drow ${ready ? 'ready' : ''}"><div><b>${q.title}</b><span>${have}/${goal} ${q.objective.noun}</span></div>${ready ? `<button class="cafe-btn turnin" data-id="${id}">turn in</button>` : ''}</div>`;
  }

  _tap(ray){
    if (this.state !== 'explore') return;
    if (checkNpcClick(ray, this.pretzel, this.bread)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-7, Math.min(7, gp.x));
    gp.z = Math.max(-6, Math.min(5, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    this.state = 'explore';
    HUD.setSubtitle('Pretzel Pit Stop');
    HUD.say('The garage smells like motor oil and fresh bread. Pick out a new ride.');
    HUD.setHint('click to walk · talk to Pretzel or head back to uptown');
    HUD.hideHintSoon(7000);
  }

  exit(){
    this.input.onTap = null;
    HUD.showEnter(null);
    HUD.say('');
    this._clearPanels();
  }

  update(dt){
    this.bread.update(dt);
    this.pretzel.update(dt, this.bread.root.position);

    let near = null, nd = Infinity;
    this.hotspots.forEach(h => {
      const d = h.pos.distanceTo(this.bread.root.position);
      if (d < nd){ nd = d; near = h; }
    });
    if (near && nd < near.r){
      const key = near.label;
      if (this.nearSpot !== key){
        this.nearSpot = key;
        HUD.showEnter(near.label, () => near.action());
      }
    } else if (this.nearSpot){
      this.nearSpot = null;
      HUD.showEnter(null);
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.5, this.bread.root.position.z * 0.5 + 1), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
